/* Builder A · WF-06 Parcours fidélité seam (Bible §21–§28, §32).
   Merchants see « Parcours fidélité »; customers just see where they are and
   what’s next. Only a committed eligible participation advances a Stage —
   scans, taps, sign-ins and Test Mode never do. */
import { useQuery } from '@tanstack/react-query'
import type { PlatformId } from '@/lib/campaign'

/* ---------- RJ-01 · journey overview ----------
   Batch 10 (06/09 · D-53): a step is ONE demande and ONE roue — no longer a
   weighted mix of variants. The demande is a review on a platform, a quiz, a
   question or a private feedback (the library of `@/lib/asks`), or nothing at
   all when the visit is a gift on its own. The roue is the campaign’s unless
   the step allocates its own. Nothing about the demande gates the gift: the
   draw happened at the scan, and every demande is skippable. */
export type DemandeKind = 'avis' | 'quiz' | 'question' | 'retour' | 'cadeau'

export interface Stage {
  id: string
  label: string
  /** what this visit asks, in the merchant’s words */
  demande: string
  demandeKind: DemandeKind
  /** the ask of the library this step serves (`getAsks()`); null for a review or a bare gift */
  askId: string | null
  /** the platform invited, when the demande is a review */
  platform: PlatformId | null
  /** the roue this step turns, by name */
  roue: string
  /** true when the step allocates a roue of its own instead of the campaign’s */
  roueOwn: boolean
}
export interface Journey {
  name: string; version: string; status: string
  stages: Stage[]
  /** the 5+ rule — unset blocks publish (§23–24) */
  plusRule: string | null
}

/* ---------- RJ-02 · stage editor ---------- */
export interface StageDetail {
  stage: string
  demande: string
  demandeKind: DemandeKind
  askId: string | null
  platform: PlatformId | null
  roue: string
  roueOwn: boolean
  roueNote: string
  fallback: string
  customerCopy: string
}

/* ---------- RJ-04 · exposure & rotation ---------- */
export interface ExposureRow { destination: string; ownWindow: string; priority: number }
export interface Exposure {
  publicFrequencyDays: number
  rows: ExposureRow[]
}

/* ---------- RJ-07 · rythme & éligibilité (real tier) ----------
   Batch 15: the sector, its floor, the window's value and the business shapes
   all live in `@/lib/scope` — the merchant chooses the sector on day one and
   the floor follows it. This endpoint no longer carries `sector`,
   `sectorReason`, `sectorDefaultDays`, `currentDays` or `presets`: they were a
   second copy of facts the seam owns, and a second copy is how two screens end
   up announcing two rhythms. What is left is what only this endpoint knows. */
export interface Rhythm {
  /** average cost of the current gift selection, in euros */
  avgGiftCost: number
  scope: string
  publicFrequencyDays: number
  sessionWindowHours: number
}

/* ---------- RJ-08 · version migration ---------- */
export interface Migration { inProgress: number; newVersion: string }

/* ---------- RJ-09 · retention report ---------- */
export interface StageReach {
  stage: string; reach: string; drop: string; giftUse: string
}
export interface Retention {
  cohort: string; matureReturnRate: string; medianDays: string
  rows: StageReach[]
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`/v1${path}`)
  if (!res.ok) throw new Error(`${res.status} on ${path}`)
  return res.json() as Promise<T>
}

export const useJourney = () =>
  useQuery({ queryKey: ['journey'], queryFn: () => get<Journey>('/journey') })
export const useStageDetail = () =>
  useQuery({ queryKey: ['journey', 'stage'], queryFn: () => get<StageDetail>('/journey/stage') })
export const useExposure = () =>
  useQuery({ queryKey: ['journey', 'exposure'], queryFn: () => get<Exposure>('/journey/exposure') })
export const useRhythm = () =>
  useQuery({ queryKey: ['journey', 'rhythm'], queryFn: () => get<Rhythm>('/journey/rhythm') })
export const useMigration = () =>
  useQuery({ queryKey: ['journey', 'migration'], queryFn: () => get<Migration>('/journey/migration') })
export const useRetention = () =>
  useQuery({ queryKey: ['journey', 'retention'], queryFn: () => get<Retention>('/journey/retention') })
